import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Button, Typography, IconButton } from '@mui/material';
import { FaAndroid, FaExternalLinkAlt } from 'react-icons/fa';
import { IoClose, IoPhonePortraitOutline } from 'react-icons/io5';
import {
  isMobileDevice,
  isAndroidDevice,
  isInsideAppWebView,
  launchApp,
  getAndroidIntentUrl,
  getCustomSchemeUrl,
} from '../utils/appLauncher';
import logoDs from '../img/logoDs.png';

export default function AutoAppOpener() {
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return;

    // 1. No ejecutar si ya está dentro de la App (WebView)
    if (isInsideAppWebView()) return;

    // 2. Solo para dispositivos móviles
    if (!isMobileDevice()) return;

    // 3. No interrumpir rutas de autenticación o callbacks
    const path = location.pathname.toLowerCase();
    const search = location.search.toLowerCase();
    if (
      path.includes('callback') ||
      path.includes('auth') ||
      search.includes('code=') ||
      search.includes('state=')
    ) {
      return;
    }

    // 4. Intento automático de apertura al entrar a la web (1 vez por ruta de sorteo o por sesión)
    const isSorteoPath = path.startsWith('/sorteo');
    const autoOpenKey = isSorteoPath ? `app_auto_open_${path}` : 'app_auto_open_done';
    const hasAttemptedAutoOpen = sessionStorage.getItem(autoOpenKey);
    if (!hasAttemptedAutoOpen) {
      sessionStorage.setItem(autoOpenKey, 'true');

      // Si es Android, ejecutamos el intent directo para abrir la app si está instalada
      if (isAndroidDevice()) {
        try {
          const intentUrl = getAndroidIntentUrl(location.pathname);
          // Usar un iframe invisible para un intento fluido o navegación por intent
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = intentUrl;
          document.body.appendChild(iframe);
          
          setTimeout(() => {
            try {
              document.body.removeChild(iframe);
            } catch (e) {}
          }, 1500);

          // También trigger suave en window.location si no abre en background
          const timer = setTimeout(() => {
            if (document.visibilityState === 'visible') {
              window.location.href = intentUrl;
            }
          }, 500);

          const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
              clearTimeout(timer);
            }
          };
          document.addEventListener('visibilitychange', handleVisibilityChange, { once: true });
        } catch (err) {
          console.warn('Auto app open error:', err);
        }
      }
    }

    // 5. Verificar si mostramos el banner de "Abrir en App"
    const bannerDismissed = sessionStorage.getItem('hide_dside_app_banner') === 'true';
    if (!bannerDismissed) {
      setShowBanner(true);
    }
  }, [location.pathname, location.search]);

  const handleOpenAppClick = () => {
    launchApp(location.pathname);
  };

  const handleDismissBanner = () => {
    setShowBanner(false);
    sessionStorage.setItem('hide_dside_app_banner', 'true');
  };

  if (!showBanner || !isMobileDevice() || isInsideAppWebView()) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 9999,
        background: 'linear-gradient(90deg, rgba(11, 15, 25, 0.98) 0%, rgba(18, 26, 43, 0.98) 100%)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(83, 252, 24, 0.25)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.6), 0 0 15px rgba(83, 252, 24, 0.15)',
        px: 1.5,
        py: 0.8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        animation: 'slideDown 0.3s ease-out',
        '@keyframes slideDown': {
          from: { transform: 'translateY(-100%)', opacity: 0 },
          to: { transform: 'translateY(0)', opacity: 1 },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0, flex: 1 }}>
        {/* Logo / Icono */}
        <Box
          component="img"
          src={logoDs}
          alt="D-SIDE"
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            border: '1px solid rgba(83, 252, 24, 0.4)',
            objectFit: 'cover',
            background: '#090d16',
            flexShrink: 0,
          }}
        />

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <Typography
              sx={{
                color: '#FFFFFF',
                fontWeight: 800,
                fontSize: '0.82rem',
                fontFamily: 'Outfit, sans-serif',
                lineHeight: 1.1,
              }}
            >
              App D-SIDE
            </Typography>
            <Box
              sx={{
                bgcolor: 'rgba(83, 252, 24, 0.15)',
                color: '#53FC18',
                fontSize: '0.62rem',
                fontWeight: 800,
                px: 0.6,
                py: 0.1,
                borderRadius: '4px',
                border: '1px solid rgba(83, 252, 24, 0.3)',
              }}
            >
              OFICIAL
            </Box>
          </Box>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.65)',
              fontSize: '0.7rem',
              fontFamily: 'Outfit, sans-serif',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Abrir en la aplicación móvil
          </Typography>
        </Box>
      </Box>

      {/* Botón Abrir & Cerrar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexShrink: 0 }}>
        <Button
          onClick={handleOpenAppClick}
          size="small"
          startIcon={<FaAndroid size={13} />}
          sx={{
            background: 'linear-gradient(135deg, #3DDC84 0%, #53FC18 100%)',
            color: '#0a0d14',
            fontWeight: 800,
            fontSize: '0.75rem',
            fontFamily: 'Outfit, sans-serif',
            textTransform: 'none',
            borderRadius: '10px',
            px: 1.4,
            py: 0.4,
            boxShadow: '0 2px 10px rgba(83, 252, 24, 0.35)',
            '&:hover': {
              background: 'linear-gradient(135deg, #53FC18 0%, #3DDC84 100%)',
            },
          }}
        >
          Abrir
        </Button>

        <IconButton
          size="small"
          onClick={handleDismissBanner}
          sx={{
            color: 'rgba(255, 255, 255, 0.5)',
            p: 0.4,
            '&:hover': {
              color: '#FFF',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
          aria-label="Cerrar banner"
        >
          <IoClose size={18} />
        </IconButton>
      </Box>
    </Box>
  );
}
